import { useMemo } from 'react'
import { generateQrModules } from '@utils/qrcode'

/**
 * QR コード表示コンポーネント (B5、Phase 6)。
 *
 * `@utils/qrcode` の自前エンコーダでモジュール行列を生成し、SVG で描画する。
 * QR は読み取り精度のため **常に黒/白固定** (テーマ色は使わない)。背景の白 rect が
 * クワイエットゾーンを兼ねるのでダーク UI 上でもそのまま読み取れる。
 */

export interface QrCodeProps {
  /** エンコードする文字列 (例: `otpauth://...`)。 */
  value: string
  /** 1 モジュールあたりの pixel サイズ (既定 5)。 */
  scale?: number
  /** アクセシビリティ用ラベル。 */
  label?: string
}

/** クワイエットゾーン (QR 規格の推奨は 4 モジュール)。 */
const QUIET = 4

export const QrCode = ({ value, scale = 5, label }: QrCodeProps): JSX.Element | null => {
  const modules = useMemo<boolean[][] | null>(() => {
    try {
      return generateQrModules(value)
    } catch {
      return null
    }
  }, [value])

  if (!modules) return null

  const size = modules.length
  const dim = size + QUIET * 2

  let path = ''
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (modules[y]![x]) path += `M${x + QUIET} ${y + QUIET}h1v1h-1z`
    }
  }

  return (
    <svg
      className="qr-code"
      viewBox={`0 0 ${dim} ${dim}`}
      width={dim * scale}
      height={dim * scale}
      role="img"
      aria-label={label ?? 'QR code'}
      shapeRendering="crispEdges"
    >
      <rect width={dim} height={dim} fill="#ffffff" />
      <path d={path} fill="#000000" />
    </svg>
  )
}
